import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PostCard } from '../components/PostCard';
import { Skeleton } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { DotRule } from '../components/DotRule';
import { Button, Container, Field, Heading, Input, Select, Stack, Text } from '../components/ui';
import { fetchDrawers, fetchFeed, type FeedCursor } from '../lib/queries';
import type { Drawer, Post } from '../lib/types';
import './Archive.css';

// "June 2026" bucket label for a post (by published_at).
function monthLabel(p: Post): string {
  const d = p.published_at ? new Date(p.published_at) : null;
  return d ? d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : 'Undated';
}

// Group posts (already sorted newest-first) into month buckets. Returns ordered
// [label, posts][] so render order matches the feed's descending order.
function groupByMonth(posts: Post[]): Array<[string, Post[]]> {
  const order: string[] = [];
  const byKey = new Map<string, Post[]>();
  for (const p of posts) {
    const label = monthLabel(p);
    if (!byKey.has(label)) {
      byKey.set(label, []);
      order.push(label);
    }
    byKey.get(label)!.push(p);
  }
  return order.map((label) => [label, byKey.get(label)!]);
}

export default function Archive() {
  const [drawers, setDrawers] = useState<Drawer[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [cursor, setCursor] = useState<FeedCursor | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Single-select filters (cleaner than the old chip row): '' = all.
  const [closetId, setClosetId] = useState('');
  const [month, setMonth] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Drawers (filter chips) load once.
  useEffect(() => {
    const ctrl = new AbortController();
    fetchDrawers(ctrl.signal)
      // Sales Room is excluded from the archive, so drop its filter chip too.
      .then((d) => { if (!ctrl.signal.aborted) setDrawers(d.filter((x) => x.slug !== 'sales-room')); })
      .catch((err) => { if (!ctrl.signal.aborted && err?.name !== 'AbortError') console.error(err); });
    return () => ctrl.abort();
  }, []);

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  // First page - refetched whenever the (debounced) search term changes.
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const page = await fetchFeed({ signal: ctrl.signal, includeSalesRoom: false, search });
        if (ctrl.signal.aborted) return;
        setPosts(page.items);
        setCursor(page.nextCursor);
        setHasMore(page.nextCursor !== null);
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        console.error('[archive] fetch failed', err);
        setError(err?.message ?? 'Something broke loading the archive.');
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [search]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !cursor) return;
    setLoadingMore(true);
    const ctrl = new AbortController();
    try {
      const page = await fetchFeed({ cursor, signal: ctrl.signal, includeSalesRoom: false, search });
      if (ctrl.signal.aborted) return;
      setPosts((prev) => [...prev, ...page.items]);
      setCursor(page.nextCursor);
      setHasMore(page.nextCursor !== null);
    } catch (err: any) {
      if (ctrl.signal.aborted || err?.name === 'AbortError') return;
      console.error('[archive] loadMore failed', err);
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, hasMore, loadingMore, search]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) loadMore(); },
      { rootMargin: '600px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadMore]);

  // Month options come from the loaded set (in feed/descending order), so the
  // dropdown only lists months that actually have pieces.
  const monthOptions = useMemo(
    () => groupByMonth(posts).map(([label]) => label),
    [posts],
  );

  // Closet + month filters are client-side over the loaded set (mirrors the old
  // feed). Keep loading more (infinite scroll) to pull in older months.
  const filtered = useMemo(() => {
    let out = posts;
    if (closetId) out = out.filter((p) => (p.drawers ?? []).some((d) => d.id === closetId));
    if (month) out = out.filter((p) => monthLabel(p) === month);
    return out;
  }, [closetId, month, posts]);

  const groups = useMemo(() => groupByMonth(filtered), [filtered]);

  return (
    <div data-ds className="archive">
      <header className="archive__header">
        <Container size="lg">
          <div className="archive__topbar">
            <Link to="/" className="archive__back">
              <Text role="small" muted as="span">&larr; Back to the closets</Text>
            </Link>
          </div>
          <Heading level="h1">Everything in the room.</Heading>
          <DotRule className="archive__rule" />
        </Container>
      </header>

      <Container size="lg">
        <div className="archive__controls">
          <Field className="archive__search">
            <span className="archive__search-icon" aria-hidden="true">
              <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2}>
                <circle cx="9" cy="9" r="6" />
                <line x1="13.5" y1="13.5" x2="18" y2="18" strokeLinecap="round" />
              </svg>
            </span>
            <Input
              type="search"
              placeholder="Search the archive..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search the archive"
              className="pl-8"
            />
          </Field>

          <Select
            value={closetId}
            onChange={(e) => setClosetId(e.target.value)}
            aria-label="Filter by closet"
            className="archive__filter"
          >
            <option value="">All closets</option>
            {drawers.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </Select>

          <Select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            aria-label="Filter by month"
            className="archive__filter"
          >
            <option value="">All months</option>
            {monthOptions.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </Select>
        </div>

        {loading ? (
          <div className="archive__grid" aria-busy="true" aria-label="Loading the archive">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="archive__cell">
                <Skeleton variant="card" aspectRatio={16 / 9} />
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState eyebrow="Error" headline="We couldn't load the archive." body={error} />
        ) : filtered.length === 0 ? (
          <EmptyState
            eyebrow={search ? 'No matches' : 'Nothing here'}
            headline={search ? `Nothing matches "${search}".` : 'The archive is empty, for now.'}
            body={search ? 'Try a different search, or clear your filters.' : 'Published pieces will collect here over time.'}
          />
        ) : (
          <Stack gap="section">
            {groups.map(([label, items]) => (
              <section key={label}>
                <Text role="eyebrow" as="h2" muted className="archive__month-label">{label}</Text>
                <div className="archive__grid">
                  {items.map((post) => (
                    <div key={post.id} className="archive__cell">
                      <PostCard post={post} ratio="4 / 3" />
                    </div>
                  ))}
                </div>
              </section>
            ))}

            {hasMore && (
              <div ref={sentinelRef} className="archive__sentinel" aria-hidden="true">
                {loadingMore ? (
                  <Text role="small" muted>Loading more...</Text>
                ) : (
                  <Button type="button" variant="ghost" size="sm" onClick={loadMore}>
                    Load more
                  </Button>
                )}
              </div>
            )}
          </Stack>
        )}
      </Container>
    </div>
  );
}
